import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Icon,
  makeStyles,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core'
import { useDebounce } from '@fuse/hooks'
import { FormattedMessage, useIntl } from 'react-intl'
import clsx from 'clsx'
import { CLOSE, SECONDARY } from 'app/constants/button'
import useSelectTopics from 'app/main/subscribersV4/subscriberDetail/useSelectTopics'
import CheckBox from 'app/componentV4/CheckBox'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import { PersonSearchIcon } from 'app/components/icons'
import { isEmpty } from 'ramda'

const useStyles = makeStyles((theme) => ({
  content: {
    minHeight: '200px',
  },
  search: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.colors['primary-350'],
    },
  },
}))

const PAGE_LIMIT = 20

const SelectTopics = (props) => {
  const classes = useStyles()
  const { open, value, onConfirm, onCloseDialog, isSubmitting } = props
  const [search, setSearch] = useState('')
  const { formatMessage } = useIntl()
  const {
    state: { topics, selected, isLoading, meta, isLoadmore },
    startProcess,
    onFetchTopics,
    onSelectTopics,
    loadMore,
    onSearchChange,
    clearState,
  } = useSelectTopics(value)

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(selected)
    }
  }

  const onSearchTopics = useDebounce((value) => {
    onSearchChange({
      topics,
      page: 1,
      limit: PAGE_LIMIT,
      keyword: value,
    })
  }, 1000)

  useEffect(() => {
    if (open) {
      startProcess({
        topics,
        page: meta.currentPage,
        limit: PAGE_LIMIT,
        search: '',
        value,
      })
    } else {
      setSearch('')
      clearState()
    }
  }, [open, onFetchTopics])

  return (
    <div>
      <Dialog
        open={open}
        onClose={onCloseDialog}
        fullWidth
        maxWidth="sm"
        classes={{
          paper: clsx('w-screen h-screen', 'flex flex-col'),
        }}
      >
        <DialogTitle
          disableTypography={true}
          className="p-5 px-10 text-20 font-medium"
        >
          <div className="flex items-center space-x-8 justify-between">
            <span className="flex items-center space-x-8 text-blue-950">
              <FormattedMessage id="subscriberCrm.editTopics.title" />
            </span>

            <div className="flex items-center">
              <IconButton
                aria-label="close"
                className="p-4"
                onClick={onCloseDialog}
              >
                <Icon className="text-24">close</Icon>
              </IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <div
            className={clsx(
              'border border-primary-350 rounded-5 flex flex-col h-full',
              classes.content,
            )}
          >
            <div className={clsx('p-5', classes.search)}>
              <SimpleInput
                id="search-group-in-crm-detail"
                name="search"
                value={search}
                placeholder={formatMessage({
                  id: 'subscriberCrm.editTopics.search.label',
                })}
                size="small"
                fullWidth
                onChange={(event) => {
                  setSearch(event.target.value)
                  onSearchTopics(event.target.value)
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <PersonSearchIcon
                      className="w-24 text-center"
                      color={isEmpty(search) ? '#A8A8A8' : '#183560'}
                    />
                  </InputAdornment>
                }
              />
            </div>
            {isLoading ? (
              <div className="flex flex-1 justify-center items-center">
                <CircularProgress size={24} />
              </div>
            ) : (
              <div className="overflow-y-auto">
                {isEmpty(topics) ? (
                  <div className="p-10 flex justify-center text-blue-950">
                    <FormattedMessage id="subscriberDetail.search.topics.empty.label" />
                  </div>
                ) : (
                  topics.map((item) => (
                    <div key={item.id} className="mb-4">
                      <div className="flex items-center">
                        <CheckBox
                          onChange={() => onSelectTopics(item.id)}
                          name={`topics-${item.id}`}
                          checked={selected.includes(item.id)}
                          inputProps={{
                            'data-testid': `topics-${item.id}`,
                          }}
                        />
                        <label
                          htmlFor={`topics-${item.id}`}
                          className="font-bold"
                        >
                          {item.name}
                        </label>
                      </div>
                    </div>
                  ))
                )}
                {meta.currentPage < meta.lastPage &&
                  (isLoadmore ? (
                    <div className="flex flex-1 justify-center items-center">
                      <CircularProgress size={20} />
                    </div>
                  ) : (
                    <div
                      className="flex space-x-2 px-5 py-4 text-center cursor-pointer opacity-50"
                      onClick={() =>
                        loadMore({
                          topics,
                          page: meta.currentPage,
                          limit: PAGE_LIMIT,
                        })
                      }
                    >
                      <Icon className="text-18">refresh</Icon>
                      <div className="m-auto">
                        <FormattedMessage id="notificationTimeline.loadMore.label" />
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions className="justify-center pb-10">
          <ContainedButton
            color={CLOSE}
            disabled={isSubmitting}
            onClick={() => onCloseDialog()}
            className="w-128 normal-case h-40"
          >
            <FormattedMessage id="text.close" />
          </ContainedButton>
          <ContainedButton
            color={SECONDARY}
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={() => handleConfirm()}
            className="normal-case h-40"
          >
            <FormattedMessage id="subscriberCrm.editGroup.save.label" />
          </ContainedButton>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default SelectTopics
